import { MatchmakingMessage, MatchmakingMessageType, websocket, InvitationMessageType } from '@playcastdotio/Messaging';
import { profileState } from '../../../stores/lobbyStores';
import { autoJoinState, authStore, joinState } from '../../../stores/globalStores';
import { invitationState } from '../../../stores/lobbyStores';
import { getPersistentPlayerId } from '../../..';
import { sendMessage } from '..';
import { queryInvitation } from './invitationMessage';
import { IdentityPlatform } from '@playcastdotio/CoreTypes';
import constants from '@playcastdotio/GlobalConfig';
import { invitationDiagnosticsCache } from '../invitationDiagnosticsCache';
import { setPlayerBroadcastId } from '../../playerObservability';

const unsubscribeFromInvitations = (invitationIds: string[]) => {
  if (invitationIds.length === 0) return;

  sendMessage(
    websocket.generators.invitation(InvitationMessageType.INVITATION_UNSUBSCRIBE, {
      invitationIds,
    }),
  );
};

// Function to kick a player from an invitation
export const kickPlayer = (invitationId: string, playerId: string, reason?: string) => {
  console.log(`Kicking player ${playerId} from invitation ${invitationId}`);
  sendMessage(
    websocket.generators.matchmaking(MatchmakingMessageType.MATCHMAKER_KICK_PLAYER, {
      invitationId,
      playerId,
      reason,
    }),
  );
};

// Helper function to handle redirects (duplicated from player.ts to avoid circular imports)
const handleRedirect = (invitationId: string, defaultAlias?: string, defaultAliasPlatform?: IdentityPlatform) => {
  // Save the invitation id to local storage
  localStorage.setItem('lastRedirectedInvitationId', invitationId);

  const doesUrlContainPavilion = window.location.href.includes('pavilion');

  // Redirect to different urls if we arent on pavilion
  if (!doesUrlContainPavilion) {
    // constants.PLAYER_BASE_URL
    if (defaultAlias) {
      switch (defaultAliasPlatform) {
        case 'clerk': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}p/${defaultAlias}`;
          break;
        }
        case 'discord': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}pd/${defaultAlias}`;
          break;
        }
        case 'twitch': {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}pt/${defaultAlias}`;
          break;
        }
        default: {
          window.location.href = `${constants.PRIME_PLAYER_BASE_URL}p/${defaultAlias}`;
          break;
        }
      }
    } else {
      window.location.href = constants.PRIME_PLAYER_BASE_URL;
    }
    return; // Exit early for non-pavilion URLs
  }

  // If we're on pavilion, use pavilion-specific redirect logic
  if (defaultAlias) {
    // Redirect to their alias page
    switch (defaultAliasPlatform) {
      case 'clerk': {
        window.location.href = `/p/${defaultAlias}`;
        break;
      }
      case 'discord': {
        window.location.href = `/pd/${defaultAlias}`;
        break;
      }
      case 'twitch': {
        window.location.href = `/pt/${defaultAlias}`;
        break;
      }
      default: {
        window.location.href = `/p/${defaultAlias}`;
        break;
      }
    }
  } else {
    // No profile to redirect to, go to 404
    window.location.href = '/404';
  }
};

export const handleMatchmakerMessage = async (message: MatchmakingMessage) => {
  console.log('Matchmaking message:', message);

  switch (message.type) {
    case MatchmakingMessageType.MATCHMAKER_INVITATION_CREATED: {
      console.log('Matchmaker invitation created:', message);
      if (message.invitation) {
        // Cache the new invitation for diagnostics
        console.log('Caching created invitation for diagnostics:', message.invitation.id);
        invitationDiagnosticsCache.set(message.invitation.id, message.invitation, 'updated');

        // Place the invitation at the top of the list
        const dedupedInvitations = profileState.currentState.invitations.filter((invitation) => invitation.id !== message?.invitation?.id);

        // Only set selectedInvitationId if the current user is the owner of the invitation
        const currentUserAuthId = authStore.currentState.playerAuthId;
        const persistentPlayerId = getPersistentPlayerId();
        console.log('Current user auth id:', currentUserAuthId);
        console.log('Persistent player id:', persistentPlayerId);
        console.log('Invitation owner profile id:', message.invitation.owner.profileId);

        // Check if owner matches either persistent ID (for guests) or auth ID (for logged-in users)
        const isOwner = message.invitation.owner.profileId === persistentPlayerId ||
                       message.invitation.owner.profileId === currentUserAuthId;

        profileState.updateState({
          ...profileState.currentState,
          invitations: [message.invitation, ...dedupedInvitations],
          selectedInvitationId: isOwner ? message.invitation.id : profileState.currentState.selectedInvitationId,
          creating: false,
        });


        setTimeout(() => {
          if (profileState.currentState.creating) {
            profileState.updateState({
              ...profileState.currentState,
              creating: false,
            });
          }
        }, 2000);
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED: {
      console.log('Matchmaker invitation updated:', message);
      if (message.invitationId) {
        // Check if this is a response to an auto-join attempt
        const currentAutoJoin = autoJoinState.currentState;
        const isAutoJoinResponse = currentAutoJoin.isAttempting && currentAutoJoin.invitationId === message.invitationId;

        // Check if we're in an errored state that should prevent auto-joining
        const currentJoinState = joinState.currentState.state;
        const isInErroredState =
          currentJoinState === 'playerDisconnected' || currentJoinState === 'hostDisconnected' || currentJoinState === 'error';

        if (isAutoJoinResponse && message.invitation?.type === 'cafe' && !isInErroredState) {
          console.log('Processing auto-join response for invitation:', message.invitationId);

          // Check if we were successfully added to the invitation
          const persistentPlayerId = getPersistentPlayerId();
          const playerAuthId = authStore.currentState.playerAuthId;

          console.log('Debug - Auto-join response details:', {
            invitationId: message.invitationId,
            persistentPlayerId,
            playerAuthId,
            playersInInvitation: message.invitation.manifest.players,
            playersCount: message.invitation.manifest.players?.length || 0,
          });

          const isUserInPlayers = message.invitation.manifest.players?.some((player) => {
            console.log('Debug - Checking player:', player.playerId, 'against', persistentPlayerId, 'and', playerAuthId);
            return player.playerId === persistentPlayerId || player.playerId === playerAuthId;
          });

          console.log('Debug - User in players result:', isUserInPlayers);

          if (isUserInPlayers) {
            console.log('Auto-join successful! Player is now in the lobby.');
            // Reset auto-join state
            autoJoinState.updateState({
              isAttempting: false,
              invitationId: null,
              timestamp: 0,
              retryAttempted: false,
            });

            // Set join state to connecting (same as if they were already in the lobby)
            if (joinState.currentState.state !== 'hostConnected' && joinState.currentState.state !== 'connecting') {
              joinState.updateState({
                ...joinState.currentState,
                state: 'connecting',
              });
              // Re query the invitation so we get join details
              queryInvitation(message.invitationId, IdentityPlatform.CLERK);
            }
          } else {
            console.log('Auto-join failed. Player not in the lobby, checking for race condition...');

            // Check if this might be a race condition where the player was just added but the invitation hasn't been updated yet
            // Give it a small delay and retry once
            if (!autoJoinState.currentState.retryAttempted) {
              console.log('Retrying auto-join after delay to handle potential race condition...');
              autoJoinState.updateState({
                ...autoJoinState.currentState,
                retryAttempted: true,
              });

              // Wait a short time for the invitation to be updated, then retry
              setTimeout(() => {
                queryInvitation(message.invitationId, IdentityPlatform.CLERK);
              }, 500); // 500ms delay

              return; // Don't redirect yet, wait for retry
            }

            console.log('Auto-join retry also failed. Redirecting to profile.');
            // Reset auto-join state
            autoJoinState.updateState({
              isAttempting: false,
              invitationId: null,
              timestamp: 0,
              retryAttempted: false,
            });

            // Get the invitation from invitationState to access default alias info
            const invitationInfo = Object.values(invitationState.currentState.invites || {}).find(
              (inv) => inv && typeof inv !== 'boolean' && inv.id === message.invitationId,
            );

            // Redirect to profile page
            if (invitationInfo && typeof invitationInfo !== 'boolean') {
              handleRedirect(
                message.invitationId,
                invitationState.currentState.defaultAlias,
                invitationState.currentState.defaultAliasPlatform,
              );
            } else {
              // Fallback redirect
              handleRedirect(message.invitationId);
            }
          }
        } else if (isAutoJoinResponse && isInErroredState) {
          console.log('Auto-join attempt blocked due to errored state:', currentJoinState);
          // Reset auto-join state since we're in an errored state
          autoJoinState.updateState({
            isAttempting: false,
            invitationId: null,
            timestamp: 0,
            retryAttempted: false,
          });

          // Get the invitation from invitationState to access default alias info
          const invitationInfo = Object.values(invitationState.currentState.invites || {}).find(
            (inv) => inv && typeof inv !== 'boolean' && inv.id === message.invitationId,
          );

          // Redirect to profile page since we can't auto-join in an errored state
          if (invitationInfo && typeof invitationInfo !== 'boolean') {
            handleRedirect(
              message.invitationId,
              invitationState.currentState.defaultAlias,
              invitationState.currentState.defaultAliasPlatform,
            );
          } else {
            // Fallback redirect
            handleRedirect(message.invitationId);
          }
        }

        // Continue with normal invitation update logic
        let inv = profileState.currentState.invitations.find((invitation) => invitation.id === message.invitationId);
        if (inv && inv.type === 'cafe') {
          if (message.invitation?.invalid) {
            // Remove the invitation from the list and unsubscribe
            const index = profileState.currentState.invitations.findIndex((invitation) => invitation.id === message.invitationId);
            const removedInvitation = profileState.currentState.invitations[index];

            // Cache the removed invitation for diagnostics
            if (removedInvitation) {
              console.log('Caching invalidated invitation for diagnostics:', removedInvitation.id);
              invitationDiagnosticsCache.set(removedInvitation.id, removedInvitation, 'removed');
            }

            profileState.updateState({
              ...profileState.currentState,
              lastInvalidatedInvitationId: message.invitationId,
              invitations: [
                ...profileState.currentState.invitations.slice(0, index),
                ...profileState.currentState.invitations.slice(index + 1),
              ],
            });
            // Unsubscribe from the removed invitation
            unsubscribeFromInvitations([removedInvitation.id]);
          } else {
            inv = {
              ...inv,
              ...message.invitation,
              manifest: {
                ...inv.manifest,
                ...message.invitation?.manifest,
              },
            } as typeof inv;
            const index = profileState.currentState.invitations.findIndex((invitation) => invitation.id === message.invitationId);
            profileState.updateState({
              ...profileState.currentState,
              invitations: [
                ...profileState.currentState.invitations.slice(0, index),
                inv,
                ...profileState.currentState.invitations.slice(index + 1),
              ],
            });
            // Cache the updated invitation for diagnostics
            console.log('Caching updated invitation for diagnostics:', inv.id);
            invitationDiagnosticsCache.set(inv.id, inv, 'updated');
          }
        }
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_FOUND: {
      console.log('Matchmaker match found:', message);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: undefined,
        matchFoundId: message.invitationId,
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_QUEUE_STATUS: {
      console.log('Matchmaker queue status:', message);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: message.invitationId,
        noHostsAvailable: message.noHostsAvailable || false, // Flag when no immediate match found
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_FAILED: {
      console.log('Matchmaker match failed:', message);
      profileState.updateState({
        ...profileState.currentState,
        noHostsAvailable: true,
        matchFailed: { reason: message.reason, canRetry: message.canRetry },
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_READY: {
      console.log('Matchmaker match ready:', message);
      setPlayerBroadcastId(message.broadcastId);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: undefined,
        matchFoundId: message.invitationId,
        matchBroadcastId: message.broadcastId,
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_PLAYER_KICKED: {
      console.log('Player kicked from matchmaking:', message);
      // The server has confirmed that a player was kicked
      // We can show a notification or update the UI here if needed
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_ERROR: {
      console.log('Matchmaker error:', message);

      // Check if this error is related to an auto-join attempt
      const currentAutoJoin = autoJoinState.currentState;
      if (currentAutoJoin.isAttempting && message.invitationId === currentAutoJoin.invitationId) {
        console.log('Auto-join failed with error:', message.error);

        // Reset auto-join state
        autoJoinState.updateState({
          isAttempting: false,
          invitationId: null,
          timestamp: 0,
        });

        // Redirect to profile page since auto-join failed
        const invitationInfo = Object.values(invitationState.currentState.invites || {}).find(
          (inv) => inv && typeof inv !== 'boolean' && inv.id === message.invitationId,
        );

        if (invitationInfo && typeof invitationInfo !== 'boolean') {
          handleRedirect(
            message.invitationId,
            invitationState.currentState.defaultAlias,
            invitationState.currentState.defaultAliasPlatform,
          );
        } else {
          // Fallback redirect
          handleRedirect(message.invitationId);
        }
      } else {
        // If we're currently connecting and get a matchmaker error, show error state
        if (joinState.currentState.state === 'connecting') {
          joinState.updateState({
            ...joinState.currentState,
            state: 'error',
          });
        }
      }
      break;
    }
  }
};
